import { trimLeadingAndTrailingSlash, trimSearchParams } from '@ms/yammer-libs-url-path-support';
import { reportUsage } from '@ms/yammer-telemetry';

import { getDeepLinkTargetPath } from './deepLinkTargetPath';
import type { CrossAppDeepLinkDetails } from './types';

export default class CrossAppDeepLink {
  private deepLinkType: CrossAppDeepLinkDetails['deepLinkType'];
  private path: CrossAppDeepLinkDetails['path'];
  private params: CrossAppDeepLinkDetails['params'];
  private targetType: CrossAppDeepLinkDetails['targetType'];
  private targetInstance: CrossAppDeepLinkDetails['targetInstance'];

  public static parse(subEntityId: string): CrossAppDeepLink | undefined {
    const { deepLinkType, path, params, targetType, targetInstance } = CrossAppDeepLink.getDeepLinkDetails(subEntityId);

    if (CrossAppDeepLink.isValid({ deepLinkType, path, targetType })) {
      return new CrossAppDeepLink({ deepLinkType, path, params, targetType, targetInstance });
    }

    return undefined;
  }

  private constructor(deepLinkDetails: CrossAppDeepLinkDetails) {
    const { deepLinkType, path, params, targetType, targetInstance } = deepLinkDetails;

    this.deepLinkType = deepLinkType;
    this.path = path ? trimLeadingAndTrailingSlash(trimSearchParams(path)) : undefined;
    this.params = params;
    this.targetType = targetType;
    this.targetInstance = targetInstance ? trimLeadingAndTrailingSlash(targetInstance) : undefined;
  }

  private static getDeepLinkDetails = (subEntityId: string): CrossAppDeepLinkDetails | Record<string, never> => {
    try {
      const decodedSubEntityId = CrossAppDeepLink.getDecodedSubEntityId(subEntityId);

      return JSON.parse(decodedSubEntityId);
    } catch {
      return {};
    }
  };

  private static isValid = (deepLinkDetails: CrossAppDeepLinkDetails): boolean => {
    const { deepLinkType, path, targetType } = deepLinkDetails;

    if (deepLinkType !== 'crossapp') {
      return false;
    }

    if (path == null && targetType == null) {
      return false;
    }

    return true;
  };

  private static getDecodedSubEntityId = (subEntityId: string): string => {
    if (subEntityId !== decodeURIComponent(subEntityId)) {
      return decodeURIComponent(subEntityId);
    }

    return subEntityId;
  };

  private getQueryParams = (): string => {
    const params = this.params;

    if (typeof params !== 'object' || Object.keys(params).length === 0) {
      return '';
    }

    const urlSearchParams = new URLSearchParams(params);

    return `?${urlSearchParams}`;
  };

  public reportUsageEvent = (): void => {
    const params = this.params;
    const integrationOrigin = params?.trk_integration_origin;
    const outlookOrigin = params?.trk_outlook_origin;
    const origin = integrationOrigin ?? outlookOrigin;
    const scenarioType = params?.trk_scenario_type;
    const scenarioSubType = params?.trk_scenario_sub_type;

    reportUsage({
      eventName: 'page_loaded_from_link_click',
      eventProperties: {
        integrationOrigin: origin,
        scenarioType,
        scenarioSubType,
      },
    });
  };

  public getTargetPath = (): string | undefined => {
    const params = this.getQueryParams();

    if (this.targetType == null) {
      return `/${this.path}${params}`;
    }

    const targetPath = getDeepLinkTargetPath({
      targetType: this.targetType,
      targetInstance: this.targetInstance,
      deepLinkType: this.deepLinkType,
    });

    return `${targetPath}${params}`;
  };
}
